import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus, Plus, X, Users } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, Brush
} from "recharts";
import { CLASS_COLORS } from "@/lib/constants";
import { getPlayerProgression, getTeamAverageTrend } from "@/lib/api";

// Up to 3 comparison overlay colors, picked to read well on dark
const COMPARE_COLORS = ["#60a5fa", "#fbbf24", "#a78bfa"];
const TEAM_AVG_COLOR = "#94a3b8"; // slate-400

const METRIC_OPTIONS = [
  { value: "dps", label: "DPS", color: "#ff4444" },
  { value: "total_damage", label: "Total Damage", color: "#ff8800" },
  { value: "hps", label: "HPS", color: "#1eff00" },
  { value: "total_healing", label: "Total Healing", color: "#00cc88" },
  { value: "parse_pct", label: "Parse %", color: "#a335ee" },
  { value: "deaths", label: "Deaths", color: "#ff2222" },
  { value: "interrupted_spells", label: "Interrupts", color: "#69CCF0" },
  { value: "avoidable_damage", label: "Avoidable Damage", color: "#ff6600" },
];

function ParseBar({ value }) {
  if (value == null) return <span className="text-xs text-muted-foreground font-mono">—</span>;
  const color = value >= 90 ? "#ff8000" : value >= 75 ? "#a335ee" : value >= 50 ? "#0070dd" : value >= 25 ? "#1eff00" : "#9d9d9d";
  return (
    <div className="flex items-center gap-2">
      <div className="parse-bar flex-1 rounded-sm" style={{ width: 80 }}>
        <div className="parse-bar-fill rounded-sm" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-mono font-bold" style={{ color }}>{value}%</span>
    </div>
  );
}

function ParseLabel({ value }) {
  if (value == null) return null;
  if (value >= 99) return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">Legendary</Badge>;
  if (value >= 95) return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">Epic</Badge>;
  if (value >= 75) return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">Rare</Badge>;
  if (value >= 50) return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">Uncommon</Badge>;
  if (value >= 25) return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 text-xs">Common</Badge>;
  return <Badge className="bg-gray-700/20 text-gray-500 border-gray-700/30 text-xs">Poor</Badge>;
}

function CustomChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  // payload[0] has the underlying data point
  const point = payload[0]?.payload || {};
  return (
    <div className="bg-[#0f1623] border border-border/50 rounded-sm px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {point.date && <p className="text-[10px] text-muted-foreground/70 mb-1 font-mono">{point.date}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-mono" style={{ color: p.color }}>
          {p.name}: {p.value == null ? "—" : (typeof p.value === "number" ? p.value.toLocaleString() : p.value)}
        </p>
      ))}
    </div>
  );
}

export default function PerformanceTab({ data, progression, allPlayers = [], onSave }) {
  const [selectedMetric, setSelectedMetric] = useState("dps");
  // Comparison overlay state: array of {id, name, wow_class, byRaid: {raid_id -> prd}}
  const [compareList, setCompareList] = useState([]);
  const [compareSearch, setCompareSearch] = useState("");
  const [comparePickerOpen, setComparePickerOpen] = useState(false);

  const currentPlayerId = progression?.[0]?.player_id;

  // Team-average toggle state
  const [teamAvgOn, setTeamAvgOn] = useState(false);
  const [teamAvgByRaid, setTeamAvgByRaid] = useState({}); // raid_id -> trend row

  useEffect(() => {
    if (!teamAvgOn || !currentPlayerId) return;
    let cancelled = false;
    getTeamAverageTrend(currentPlayerId).then((res) => {
      if (cancelled) return;
      const map = {};
      for (const row of res.trend || []) map[row.raid_id] = row;
      setTeamAvgByRaid(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [teamAvgOn, currentPlayerId]);

  const parsePct = data?.parse_pct;
  const dps = data?.dps;
  const hps = data?.hps;
  const isHealer = hps > 0 && (hps > dps || dps === 0 || dps == null);

  const chartData = useMemo(() => {
    // Build unique labels: append a short date so multiple raids with the same
    // boss_name/raid_name don't collapse onto the same X position in Recharts.
    const filtered = progression.filter((p) => p.raid_info);
    const labelCounts = {};
    return filtered.map((p) => {
      const base = p.raid_info.boss_name || p.raid_info.raid_name?.substring(0, 20) || p.raid_info.date || "Raid";
      const dateStr = p.raid_info.date || "";
      // Short date suffix like "02-04" for disambiguation
      const short = dateStr ? dateStr.slice(5) : "";
      let label = short ? `${base} (${short})` : base;
      // Final safety: if still duplicated (same boss + same date), append a counter
      labelCounts[label] = (labelCounts[label] || 0) + 1;
      if (labelCounts[label] > 1) label = `${label} #${labelCounts[label]}`;
      const row = {
        label,
        raid_id: p.raid_id,
        date: p.raid_info.date,
        raid: p.raid_info.raid_name,
        boss: p.raid_info.boss_name,
        parse_pct: p.parse_pct ?? null,
        dps: p.dps ? Math.round(p.dps) : null,
        total_damage: p.total_damage || null,
        hps: p.hps ? Math.round(p.hps) : null,
        total_healing: p.total_healing || null,
        deaths: p.combat_stats?.deaths || 0,
        interrupted_spells: p.interrupted_spells || 0,
        avoidable_damage: p.combat_stats?.avoidable_damage || 0,
      };
      // Merge comparison player values for the same raid_id
      for (const cmp of compareList) {
        const otherPrd = cmp.byRaid[p.raid_id];
        if (otherPrd) {
          row[`dps_${cmp.id}`] = otherPrd.dps ? Math.round(otherPrd.dps) : null;
          row[`hps_${cmp.id}`] = otherPrd.hps ? Math.round(otherPrd.hps) : null;
          row[`total_damage_${cmp.id}`] = otherPrd.total_damage || null;
          row[`total_healing_${cmp.id}`] = otherPrd.total_healing || null;
          row[`parse_pct_${cmp.id}`] = otherPrd.parse_pct ?? null;
          row[`deaths_${cmp.id}`] = otherPrd.combat_stats?.deaths || 0;
          row[`interrupted_spells_${cmp.id}`] = otherPrd.interrupted_spells || 0;
          row[`avoidable_damage_${cmp.id}`] = otherPrd.combat_stats?.avoidable_damage || 0;
        } else {
          // Player didn't attend this raid — null so the line skips it
          row[`${selectedMetric}_${cmp.id}`] = null;
        }
      }
      // Team-average overlay: role-peer median for the player's role this raid
      const teamRow = teamAvgByRaid[p.raid_id];
      if (teamRow) {
        const isHealerRaid = teamRow.role === "healer";
        row.team_avg_dps = isHealerRaid ? null : teamRow.peer_dps_median;
        row.team_avg_hps = isHealerRaid ? teamRow.peer_hps_median : null;
      } else {
        row.team_avg_dps = null;
        row.team_avg_hps = null;
      }
      return row;
    });
  }, [progression, compareList, selectedMetric, teamAvgByRaid]);

  // Are there any non-null values for the selected metric on the main player?
  const hasMetricData = useMemo(
    () => chartData.some((d) => d[selectedMetric] != null && d[selectedMetric] !== 0),
    [chartData, selectedMetric]
  );

  // Comparison player picker — restrict to OTHER players on same team
  const compareCandidates = useMemo(() => {
    if (!comparePickerOpen) return [];
    const usedIds = new Set([currentPlayerId, ...compareList.map((c) => c.id)]);
    return allPlayers
      .filter((p) => !usedIds.has(p.id))
      .filter((p) => !compareSearch || p.name.toLowerCase().includes(compareSearch.toLowerCase()))
      .slice(0, 30);
  }, [allPlayers, compareList, currentPlayerId, compareSearch, comparePickerOpen]);

  const addCompare = async (p) => {
    if (compareList.length >= 3) return;
    try {
      const res = await getPlayerProgression(p.id);
      const byRaid = {};
      for (const row of res.progression || []) {
        byRaid[row.raid_id] = row;
      }
      setCompareList((prev) => [...prev, { id: p.id, name: p.name, wow_class: p.wow_class, byRaid }]);
      setComparePickerOpen(false);
      setCompareSearch("");
    } catch (e) {
      console.error("Failed to load comparison player:", e);
    }
  };

  const removeCompare = (id) => {
    setCompareList((prev) => prev.filter((c) => c.id !== id));
  };


  const metricConfig = METRIC_OPTIONS.find((m) => m.value === selectedMetric) || METRIC_OPTIONS[0];

  // Compare with previous
  const currentIdx = progression.findIndex((p) => p.raid_id === data?.raid_id);
  const prevData = currentIdx > 0 ? progression[currentIdx - 1] : null;
  const parseDelta = parsePct != null && prevData?.parse_pct != null
    ? parsePct - prevData.parse_pct : null;
  const dpsDelta = dps != null && prevData?.dps != null
    ? dps - prevData.dps : null;
  const hpsDelta = hps != null && prevData?.hps != null
    ? hps - prevData.hps : null;

  return (
    <div className="mt-4 space-y-6" data-testid="performance-tab">
      {/* Current Performance */}
      <h3 className="heading text-lg font-semibold uppercase tracking-tight">Performance</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-card">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Parse Percentile</span>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl font-bold font-mono" data-testid="parse-pct-value">
              {parsePct != null ? `${parsePct}` : "—"}
            </span>
            <ParseLabel value={parsePct} />
            {parseDelta !== null && <DeltaBadge value={parseDelta} suffix="%" />}
          </div>
          <ParseBar value={parsePct} />
        </div>

        <div className="stat-card">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">DPS</span>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold font-mono" data-testid="dps-value">
              {dps != null && dps > 0 ? Number(dps).toLocaleString() : "—"}
            </span>
            {dpsDelta !== null && <DeltaBadge value={dpsDelta} suffix="" />}
          </div>
        </div>

        {/* HPS Card — always show when healing data exists */}
        {(hps != null && hps > 0) && (
          <div className="stat-card">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">
              HPS {isHealer && <span className="text-green-500">(Primary)</span>}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold font-mono text-green-400" data-testid="hps-value">
                {Number(hps).toLocaleString()}
              </span>
              {hpsDelta !== null && <DeltaBadge value={hpsDelta} suffix="" />}
            </div>
          </div>
        )}
      </div>

      {parsePct == null && dps == null && hps == null && (
        <p className="text-xs text-muted-foreground">Import a combat log to populate performance data</p>
      )}

      {/* Combat Log Stats */}
      {(data?.total_damage || data?.total_healing || data?.hps) && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Combat Log Data</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data?.total_damage != null && (
              <div className="stat-card">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Damage</span>
                <p className="text-lg font-bold font-mono">{Number(data.total_damage).toLocaleString()}</p>
              </div>
            )}
            {data?.dps != null && data.dps > 0 && (
              <div className="stat-card">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">DPS (from log)</span>
                <p className="text-lg font-bold font-mono">{Number(data.dps).toLocaleString()}</p>
              </div>
            )}
            {data?.total_healing != null && data.total_healing > 0 && (
              <div className="stat-card">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Healing</span>
                <p className="text-lg font-bold font-mono text-green-500">{Number(data.total_healing).toLocaleString()}</p>
              </div>
            )}
            {data?.hps != null && data.hps > 0 && (
              <div className="stat-card">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">HPS</span>
                <p className="text-lg font-bold font-mono text-green-500">{Number(data.hps).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trend Chart */}
      {chartData.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="heading text-lg font-semibold uppercase tracking-tight">Trend Chart</h3>
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger data-testid="metric-selector" className="w-48 h-8 text-xs bg-card/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                      {m.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Compare Players strip */}
          {allPlayers.length > 1 && (
            <div className="mb-3 flex items-center flex-wrap gap-2" data-testid="compare-strip">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Compare:
              </span>
              {compareList.map((c, i) => (
                <Badge
                  key={c.id}
                  data-testid={`compare-chip-${c.id}`}
                  variant="outline"
                  className="text-xs font-mono gap-1 pl-2 pr-1 py-0.5"
                  style={{ borderColor: COMPARE_COLORS[i], color: CLASS_COLORS[c.wow_class] || "#fff" }}
                >
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: COMPARE_COLORS[i] }} />
                  {c.name}
                  <button
                    data-testid={`remove-compare-${c.id}`}
                    onClick={() => removeCompare(c.id)}
                    className="ml-1 rounded hover:bg-white/10 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {compareList.length < 3 && (
                <div className="relative">
                  <Button
                    data-testid="open-compare-picker"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs rounded-sm gap-1 border-dashed"
                    onClick={() => setComparePickerOpen((v) => !v)}
                  >
                    <Plus className="h-3 w-3" /> Add player
                  </Button>
                  {comparePickerOpen && (
                    <div
                      data-testid="compare-picker"
                      className="absolute z-20 mt-1 left-0 w-64 bg-[#0f1623] border border-border/50 rounded-sm shadow-xl p-2"
                    >
                      <input
                        autoFocus
                        data-testid="compare-search-input"
                        value={compareSearch}
                        onChange={(e) => setCompareSearch(e.target.value)}
                        placeholder="Search players..."
                        className="w-full px-2 py-1 text-xs bg-card/50 border border-border/50 rounded-sm font-mono"
                      />
                      <div className="max-h-48 overflow-y-auto mt-1">
                        {compareCandidates.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2 px-1">No players</p>
                        ) : (
                          compareCandidates.map((p) => (
                            <button
                              key={p.id}
                              data-testid={`compare-candidate-${p.id}`}
                              onClick={() => addCompare(p)}
                              className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-card/60 rounded-sm"
                            >
                              <span className="text-xs font-mono" style={{ color: CLASS_COLORS[p.wow_class] || "#fff" }}>
                                {p.name}
                              </span>
                              <span className="text-[10px] text-muted-foreground">{p.wow_class}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Compare-to-team-average toggle (only for dps/hps metrics) */}
              {(selectedMetric === "dps" || selectedMetric === "hps") && (
                <Button
                  data-testid="toggle-team-avg"
                  variant={teamAvgOn ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTeamAvgOn((v) => !v)}
                  className={`h-7 text-xs rounded-sm gap-1 ${teamAvgOn ? "bg-slate-500 hover:bg-slate-600 text-white" : "border-dashed"}`}
                >
                  <Users className="h-3 w-3" /> Team Avg
                </Button>
              )}
            </div>
          )}

          <div className="stat-card p-4" data-testid="trend-chart">
            {!hasMetricData ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground" data-testid="trend-chart-empty">
                No {metricConfig.label} data for this player yet
              </div>
            ) : chartData.length === 1 ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground" data-testid="trend-chart-single">
                {metricConfig.label}: <span className="ml-2 font-mono font-bold" style={{ color: metricConfig.color }}>
                  {Number(chartData[0][selectedMetric] ?? 0).toLocaleString()}
                </span>
                <span className="ml-3 text-xs text-muted-foreground/70">(need 2+ raids for a trend line)</span>
              </div>
            ) : (
            <ResponsiveContainer width="100%" height={teamAvgOn || compareList.length > 0 || chartData.length >= 6 ? 320 : 280}>
              {selectedMetric === "deaths" || selectedMetric === "interrupted_spells" ? (
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} angle={-30} textAnchor="end" height={60} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <RTooltip content={<CustomChartTooltip />} cursor={{ fill: "rgba(100,116,139,0.08)" }} />
                  {compareList.length > 0 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  <Bar dataKey={selectedMetric} fill={metricConfig.color} name="You"
                    radius={[3, 3, 0, 0]} maxBarSize={40} />
                  {compareList.map((c, i) => (
                    <Bar key={c.id} dataKey={`${selectedMetric}_${c.id}`} fill={COMPARE_COLORS[i]} name={c.name}
                      radius={[3, 3, 0, 0]} maxBarSize={40} />
                  ))}
                  {chartData.length >= 6 && (
                    <Brush dataKey="label" height={22} stroke="#475569" fill="rgba(15,22,35,0.6)"
                      travellerWidth={8} tickFormatter={() => ""} />
                  )}
                </BarChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} angle={-30} textAnchor="end" height={60} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                  <RTooltip content={<CustomChartTooltip />} cursor={{ stroke: "#475569", strokeDasharray: "3 3" }} />
                  {(compareList.length > 0 || teamAvgOn) && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  <Line type="monotone" dataKey={selectedMetric} stroke={metricConfig.color}
                    name="You" strokeWidth={2} dot={{ r: 4, fill: metricConfig.color }}
                    activeDot={{ r: 6 }} connectNulls isAnimationActive={false} />
                  {compareList.map((c, i) => (
                    <Line key={c.id} type="monotone" dataKey={`${selectedMetric}_${c.id}`}
                      stroke={COMPARE_COLORS[i]} name={c.name} strokeWidth={2}
                      dot={{ r: 3, fill: COMPARE_COLORS[i] }} activeDot={{ r: 5 }}
                      strokeDasharray="4 3" connectNulls isAnimationActive={false} />
                  ))}
                  {teamAvgOn && (selectedMetric === "dps" || selectedMetric === "hps") && (
                    <Line type="monotone"
                      dataKey={selectedMetric === "dps" ? "team_avg_dps" : "team_avg_hps"}
                      stroke={TEAM_AVG_COLOR}
                      name="Team Median (role peers)"
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      strokeDasharray="2 4"
                      connectNulls
                      isAnimationActive={false}
                    />
                  )}
                  {chartData.length >= 6 && (
                    <Brush dataKey="label" height={22} stroke="#475569" fill="rgba(15,22,35,0.6)"
                      travellerWidth={8} tickFormatter={() => ""} />
                  )}
                </LineChart>
              )}
            </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Weekly Progression Table */}
      {chartData.length > 1 && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Weekly Progression</h3>
          <div className="space-y-1 overflow-x-auto">
            <div className="grid grid-cols-[100px_1fr_80px_80px_100px_80px_50px_50px] gap-3 px-3 py-1.5 min-w-[700px]">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Date</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Boss</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Parse</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">DPS</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Dmg</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">HPS</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Deaths</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Ints</span>
            </div>
            {chartData.map((row, i) => (
              <div key={i} data-testid={`progression-row-${i}`}
                className="grid grid-cols-[100px_1fr_80px_80px_100px_80px_50px_50px] gap-3 px-3 py-2 items-center hover:bg-card/50 min-w-[700px]">
                <span className="text-sm font-mono">{row.date}</span>
                <span className="text-sm truncate">{row.boss || row.raid}</span>
                <ParseBar value={row.parse_pct} />
                <span className="text-sm font-mono">{row.dps ? row.dps.toLocaleString() : "—"}</span>
                <span className="text-sm font-mono">{row.total_damage ? Number(row.total_damage).toLocaleString() : "—"}</span>
                <span className="text-sm font-mono text-green-500">{row.hps ? row.hps.toLocaleString() : "—"}</span>
                <span className={`text-sm font-mono ${row.deaths > 0 ? "text-destructive" : ""}`}>{row.deaths}</span>
                <span className="text-sm font-mono text-blue-400">{row.interrupted_spells}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaBadge({ value, suffix }) {
  if (value === 0) return <Minus className="h-4 w-4 text-muted-foreground" />;
  const isPositive = value > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-mono ${isPositive ? "text-green-500" : "text-destructive"}`}>
      {isPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {isPositive ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
}
